import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, HardHat, Loader2, Save, Pencil, X, Plus, Package, FolderPlus, ArrowRight,
    Mail, Phone, MapPin, StickyNote, Image as ImageIcon, Send, PackageCheck, CalendarDays, FileDown, BadgeCheck,
} from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { ADDRESS_TYPES } from '../../lib/addressTypes';
import { generateExtrasPartnerPdf } from '../../lib/extrasPartnerPdfGenerator';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';

interface EvenementielEvent {
    id: string;
    type: string;
    start_time: string;
    end_time: string;
    first_name?: string;
    last_name?: string;
    company_name?: string;
    organizer_name?: string;
}

const eventLabel = (e: EvenementielEvent) => {
    const name = e.company_name || e.organizer_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.type || 'Privatisation';
    const dt = e.start_time ? new Date(e.start_time).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
    return dt ? `${name} — ${dt}` : name;
};

/** Bouton "Faire une demande d'extras" depuis la fiche partenaire : type d'extra, quantité, date, heure
 *  de début/fin, avec possibilité de rattacher la demande à une privatisation existante (préremplit alors
 *  date/heure) — demande d'Emeni. */
function NewExtrasRequestModal({ partnerId, types, onClose, onCreated }: {
    partnerId: string; types: { id: string; label: string; category_id?: string | null }[]; onClose: () => void; onCreated: () => void;
}) {
    const [events, setEvents] = React.useState<EvenementielEvent[]>([]);
    const [extraTypeId, setExtraTypeId] = React.useState(types[0]?.id || '');
    const [quantity, setQuantity] = React.useState('1');
    const [eventId, setEventId] = React.useState('');
    const [requestDate, setRequestDate] = React.useState('');
    const [startTime, setStartTime] = React.useState('');
    const [endTime, setEndTime] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [categoryPeople, setCategoryPeople] = React.useState<{ id: string; first_name: string; last_name: string; already_used: boolean }[]>([]);
    const [selectedPersonIds, setSelectedPersonIds] = React.useState<string[]>([]);

    React.useEffect(() => {
        moduleApi.getEvenementiel().then((d: any) => setEvents(Array.isArray(d) ? d : [])).catch(() => setEvents([]));
    }, []);

    // Personnes enregistrées pour ce type précis : sélection optionnelle, "si enregistrées et si voulu" —
    // demande explicite d'Emeni (2026-08-19), en plus du type + quantité qui reste la base.
    React.useEffect(() => {
        setSelectedPersonIds([]);
        if (!extraTypeId) { setCategoryPeople([]); return; }
        let cancelled = false;
        moduleApi.getExtrasPartnerPeople(partnerId, extraTypeId).then((d: any) => {
            if (cancelled) return;
            setCategoryPeople(Array.isArray(d) ? d : []);
        }).catch(() => { if (!cancelled) setCategoryPeople([]); });
        return () => { cancelled = true; };
    }, [extraTypeId, partnerId]);

    const togglePerson = (personId: string) => {
        setSelectedPersonIds((prev) => prev.includes(personId) ? prev.filter((id) => id !== personId) : [...prev, personId]);
    };

    const handleSelectEvent = (id: string) => {
        setEventId(id);
        const ev = events.find((e) => e.id === id);
        if (ev?.start_time) {
            const start = new Date(ev.start_time);
            setRequestDate(start.toISOString().slice(0, 10));
            setStartTime(start.toISOString().slice(11, 16));
        }
        if (ev?.end_time) setEndTime(new Date(ev.end_time).toISOString().slice(11, 16));
    };

    const handleSave = async () => {
        if (!extraTypeId) { setError("Merci de choisir un type d'extra."); return; }
        setSaving(true);
        setError('');
        try {
            await moduleApi.createExtrasRequest(partnerId, {
                extra_type_id: extraTypeId, quantity: Math.max(1, Number(quantity) || 1),
                request_date: requestDate || undefined, start_time: startTime || undefined, end_time: endTime || undefined,
                event_id: eventId || undefined,
                person_ids: selectedPersonIds.length > 0 ? selectedPersonIds : undefined,
            });
            onCreated();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de la création.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} title="Faire une demande d'extras" icon={<Send size={16} />} maxWidth="max-w-md"
            footer={<>
                <Button variant="secondary" onClick={onClose}>Annuler</Button>
                <Button onClick={handleSave} disabled={saving || !extraTypeId}>
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {saving ? 'Création...' : 'Créer la demande'}
                </Button>
            </>}>
            <div className="space-y-4">
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Quantité</label>
                    <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                </div>
                {types.length === 0 ? (
                    <p className="text-xs text-warning-500">Ajoutez au moins un type d'extra au catalogue avant de faire une demande.</p>
                ) : (
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Type d'extra *</label>
                        <select value={extraTypeId} onChange={(e) => setExtraTypeId(e.target.value)}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500">
                            {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                    </div>
                )}
                {categoryPeople.length > 0 && (
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Personnes souhaitées (optionnel)</label>
                        <div className="flex flex-wrap gap-1.5">
                            {categoryPeople.map((p) => {
                                const selected = selectedPersonIds.includes(p.id);
                                return (
                                    <button type="button" key={p.id} onClick={() => togglePerson(p.id)}
                                        className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${selected ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'}`}>
                                        {p.first_name} {p.last_name}{p.already_used ? ' · déjà venu(e)' : ''}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-content-muted">Si tu veux demander quelqu'un de précis — sinon le partenaire choisit librement.</p>
                    </div>
                )}
                {events.length > 0 && (
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Rattacher à une privatisation (optionnel)</label>
                        <select value={eventId} onChange={(e) => handleSelectEvent(e.target.value)}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500">
                            <option value="">Aucune — demande libre</option>
                            {events.map((e) => <option key={e.id} value={e.id}>{eventLabel(e)}</option>)}
                        </select>
                        <p className="text-[11px] text-content-muted">Préremplit la date et les horaires ci-dessous, modifiables ensuite.</p>
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Date</label>
                        <input type="date" value={requestDate} onChange={(e) => setRequestDate(e.target.value)}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Début</label>
                        <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Fin</label>
                        <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                </div>
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            </div>
        </Modal>
    );
}

// Jetons propres au mail de demande envoyé au partenaire — {{lien_confirmation}} n'est pertinent que
// pour ce mail précis (calculé uniquement au moment de l'envoi).
const REQUEST_EMAIL_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{partenaire}}', label: 'Nom du partenaire' },
    { token: '{{extra_demande}}', label: 'Extras demandé (ex: 4 x Hôtesses (18:00 - 02:30))' },
    { token: '{{date}}', label: 'Date de la privatisation (si renseignée)' },
    { token: '{{heure_debut}}', label: 'Heure de début de la privatisation' },
    { token: '{{heure_fin}}', label: 'Heure de fin de la privatisation' },
    { token: '{{nombre_personnes}}', label: 'Nombre de personnes sur la privatisation' },
    { token: '{{lien_confirmation}}', label: 'Lien de confirmation par le partenaire' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons du mail "avec suggestion" — mêmes jetons que ci-dessus, + {{personnes_souhaitees}} qui n'a de
// sens que dans ce modèle (jamais vide puisqu'il ne se déclenche que si des personnes sont sélectionnées).
const REQUEST_WITH_SUGGESTION_EMAIL_TOKENS = [
    ...REQUEST_EMAIL_TOKENS.slice(0, 2),
    { token: '{{extra_demande}}', label: 'Extras demandé (ex: 4 x Hôtesses (18:00 - 02:30))' },
    { token: '{{personnes_souhaitees}}', label: 'Personnes souhaitées sélectionnées' },
    ...REQUEST_EMAIL_TOKENS.slice(3),
];

// Jetons propres au mail reçu par L'ÉTABLISSEMENT (pas le partenaire) quand ce partenaire confirme sa
// demande — jamais de mail client final ici, la demande est 100% interne.
const ESTABLISHMENT_CONFIRMATION_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{partenaire}}', label: 'Nom du partenaire' },
    { token: '{{type_extra}}', label: "Type d'extra" },
    { token: '{{quantite}}', label: 'Quantité' },
    { token: '{{date}}', label: 'Date de la privatisation (si renseignée)' },
    { token: '{{heure_debut}}', label: 'Heure de début' },
    { token: '{{heure_fin}}', label: 'Heure de fin' },
    { token: '{{logo}}', label: 'Votre logo' },
];

interface ExtrasType {
    id: string;
    label: string;
    category_id: string | null;
}

interface ExtrasCategory {
    id: string;
    name: string;
    type_count: number;
}

interface ExtrasPartner {
    id: string;
    name: string;
    email: string;
    phone: string;
    address_number?: string | null;
    address_type?: string | null;
    address_name?: string | null;
    address_city?: string | null;
    address_postal_code?: string | null;
    notes: string;
    contact_first_name?: string | null;
    contact_last_name?: string | null;
    contact_phone?: string | null;
    contact_email?: string | null;
    logo_url?: string | null;
    request_email_subject?: string | null;
    request_email_body?: string | null;
    request_with_suggestion_subject?: string | null;
    request_with_suggestion_body?: string | null;
    establishment_confirmation_subject?: string | null;
    establishment_confirmation_body?: string | null;
    types: ExtrasType[];
    categories: ExtrasCategory[];
}

const MAX_LOGO_DATA_URL_LENGTH = 1000000;

export const ExtrasPartnerDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [partner, setPartner] = React.useState<ExtrasPartner | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [notFound, setNotFound] = React.useState(false);

    const [isEditing, setIsEditing] = React.useState(false);
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [addressNumber, setAddressNumber] = React.useState('');
    const [addressType, setAddressType] = React.useState('');
    const [addressName, setAddressName] = React.useState('');
    const [addressCity, setAddressCity] = React.useState('');
    const [addressPostalCode, setAddressPostalCode] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [contactFirstName, setContactFirstName] = React.useState('');
    const [contactLastName, setContactLastName] = React.useState('');
    const [contactPhone, setContactPhone] = React.useState('');
    const [contactEmail, setContactEmail] = React.useState('');
    const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const [showAddCategory, setShowAddCategory] = React.useState(false);
    const [newCategoryName, setNewCategoryName] = React.useState('');
    const [savingCategory, setSavingCategory] = React.useState(false);

    const [requestCounts, setRequestCounts] = React.useState<{ toRequest: number; requested: number; confirmed: number } | null>(null);
    const [showNewRequest, setShowNewRequest] = React.useState(false);

    const [showEmailSettings, setShowEmailSettings] = React.useState(false);
    const [emailSettingsTab, setEmailSettingsTab] = React.useState<'partenaire' | 'partenaire_suggestion' | 'etablissement'>('partenaire');
    const [requestEmailSubject, setRequestEmailSubject] = React.useState('');
    const [requestEmailBody, setRequestEmailBody] = React.useState('');
    const [requestWithSuggestionSubject, setRequestWithSuggestionSubject] = React.useState('');
    const [requestWithSuggestionBody, setRequestWithSuggestionBody] = React.useState('');
    const [establishmentConfirmationSubject, setEstablishmentConfirmationSubject] = React.useState('');
    const [establishmentConfirmationBody, setEstablishmentConfirmationBody] = React.useState('');
    const [savingRequestEmail, setSavingRequestEmail] = React.useState(false);
    const [requestEmailActiveField, setRequestEmailActiveField] = React.useState<'subject' | 'body'>('body');
    const requestEmailSubjectRef = React.useRef<HTMLInputElement>(null);
    const requestEmailBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const insertRequestEmailToken = (token: string) => {
        if (requestEmailActiveField === 'subject') insertNotificationToken(requestEmailSubjectRef.current, requestEmailSubject, setRequestEmailSubject, token);
        else insertNotificationToken(requestEmailBodyRef.current, requestEmailBody, setRequestEmailBody, token);
    };
    const wrapRequestEmailBody = (marker: string) => wrapNotificationSelection(requestEmailBodyRef.current, requestEmailBody, setRequestEmailBody, marker);
    const [requestWithSuggestionActiveField, setRequestWithSuggestionActiveField] = React.useState<'subject' | 'body'>('body');
    const requestWithSuggestionSubjectRef = React.useRef<HTMLInputElement>(null);
    const requestWithSuggestionBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const insertRequestWithSuggestionToken = (token: string) => {
        if (requestWithSuggestionActiveField === 'subject') insertNotificationToken(requestWithSuggestionSubjectRef.current, requestWithSuggestionSubject, setRequestWithSuggestionSubject, token);
        else insertNotificationToken(requestWithSuggestionBodyRef.current, requestWithSuggestionBody, setRequestWithSuggestionBody, token);
    };
    const wrapRequestWithSuggestionBody = (marker: string) => wrapNotificationSelection(requestWithSuggestionBodyRef.current, requestWithSuggestionBody, setRequestWithSuggestionBody, marker);
    const [establishmentConfirmationActiveField, setEstablishmentConfirmationActiveField] = React.useState<'subject' | 'body'>('body');
    const establishmentConfirmationSubjectRef = React.useRef<HTMLInputElement>(null);
    const establishmentConfirmationBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const insertEstablishmentConfirmationToken = (token: string) => {
        if (establishmentConfirmationActiveField === 'subject') insertNotificationToken(establishmentConfirmationSubjectRef.current, establishmentConfirmationSubject, setEstablishmentConfirmationSubject, token);
        else insertNotificationToken(establishmentConfirmationBodyRef.current, establishmentConfirmationBody, setEstablishmentConfirmationBody, token);
    };
    const wrapEstablishmentConfirmationBody = (marker: string) => wrapNotificationSelection(establishmentConfirmationBodyRef.current, establishmentConfirmationBody, setEstablishmentConfirmationBody, marker);

    const load = React.useCallback(() => {
        if (!id) return;
        moduleApi.getExtrasPartner(id).then((p: any) => {
            if (!p) { setNotFound(true); return; }
            setPartner(p);
            setName(p.name || '');
            setEmail(p.email || '');
            setPhone(p.phone || '');
            setAddressNumber(p.address_number || '');
            setAddressType(p.address_type || '');
            setAddressName(p.address_name || '');
            setAddressCity(p.address_city || '');
            setAddressPostalCode(p.address_postal_code || '');
            setNotes(p.notes || '');
            setContactFirstName(p.contact_first_name || '');
            setContactLastName(p.contact_last_name || '');
            setContactPhone(p.contact_phone || '');
            setContactEmail(p.contact_email || '');
            setLogoUrl(p.logo_url || null);
            setRequestEmailSubject(p.request_email_subject || '');
            setRequestEmailBody(p.request_email_body || '');
            setRequestWithSuggestionSubject(p.request_with_suggestion_subject || '');
            setRequestWithSuggestionBody(p.request_with_suggestion_body || '');
            setEstablishmentConfirmationSubject(p.establishment_confirmation_subject || '');
            setEstablishmentConfirmationBody(p.establishment_confirmation_body || '');
        }).catch(() => setNotFound(true)).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        if (!id) return;
        moduleApi.getExtrasPartnerRequests(id).then((d: any) => {
            setRequestCounts({
                toRequest: Array.isArray(d?.a_demander) ? d.a_demander.length : 0,
                requested: Array.isArray(d?.demandees) ? d.demandees.length : 0,
                confirmed: Array.isArray(d?.confirmees_par_prestataire) ? d.confirmees_par_prestataire.length : 0,
            });
        }).catch(() => setRequestCounts({ toRequest: 0, requested: 0, confirmed: 0 }));
    }, [id]);

    const startEdit = () => {
        if (!partner) return;
        setName(partner.name || '');
        setEmail(partner.email || '');
        setPhone(partner.phone || '');
        setAddressNumber(partner.address_number || '');
        setAddressType(partner.address_type || '');
        setAddressName(partner.address_name || '');
        setAddressCity(partner.address_city || '');
        setAddressPostalCode(partner.address_postal_code || '');
        setNotes(partner.notes || '');
        setContactFirstName(partner.contact_first_name || '');
        setContactLastName(partner.contact_last_name || '');
        setContactPhone(partner.contact_phone || '');
        setContactEmail(partner.contact_email || '');
        setLogoUrl(partner.logo_url || null);
        setError('');
        setIsEditing(true);
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const base64 = ev.target?.result as string;
            if (base64.length > MAX_LOGO_DATA_URL_LENGTH) {
                setError('Logo trop volumineux (max 1 Mo).');
                return;
            }
            setLogoUrl(base64);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleSave = async () => {
        if (!id || !name.trim()) { setError('Le nom est requis.'); return; }
        setSaving(true);
        setError('');
        try {
            await moduleApi.updateExtrasPartner(id, {
                name: name.trim(), email: email.trim(), phone: phone.trim(),
                address_number: addressNumber.trim(), address_type: addressType.trim(),
                address_name: addressName.trim(), address_city: addressCity.trim(),
                address_postal_code: addressPostalCode.trim(),
                notes: notes.trim(), logo_url: logoUrl || '',
                contact_first_name: contactFirstName.trim(), contact_last_name: contactLastName.trim(),
                contact_phone: contactPhone.trim(), contact_email: contactEmail.trim()
            });
            setIsEditing(false);
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveRequestEmail = async () => {
        if (!id) return;
        setSavingRequestEmail(true);
        setError('');
        try {
            await moduleApi.updateExtrasPartner(id, {
                request_email_subject: requestEmailSubject, request_email_body: requestEmailBody,
                request_with_suggestion_subject: requestWithSuggestionSubject, request_with_suggestion_body: requestWithSuggestionBody,
                establishment_confirmation_subject: establishmentConfirmationSubject, establishment_confirmation_body: establishmentConfirmationBody,
            });
            setShowEmailSettings(false);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSavingRequestEmail(false);
        }
    };

    const handleAddCategory = async () => {
        if (!id || !newCategoryName.trim()) return;
        setSavingCategory(true);
        try {
            await moduleApi.createExtrasCategory(id, { name: newCategoryName.trim() });
            setNewCategoryName('');
            setShowAddCategory(false);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible de créer cette catégorie.");
        } finally {
            setSavingCategory(false);
        }
    };

    const formatAddress = (num: string | null | undefined, type: string | null | undefined, n: string | null | undefined, city: string | null | undefined, postal: string | null | undefined) => {
        return [num, type, n, postal, city].filter(Boolean).join(' ');
    };

    const handleDownloadPdf = () => {
        if (!id || !partner) return;
        const currentAddressNumber = isEditing ? addressNumber : partner.address_number;
        const currentAddressType = isEditing ? addressType : partner.address_type;
        const currentAddressName = isEditing ? addressName : partner.address_name;
        const currentAddressCity = isEditing ? addressCity : partner.address_city;
        const currentAddressPostalCode = isEditing ? addressPostalCode : partner.address_postal_code;
        generateExtrasPartnerPdf({
            id, name: (isEditing ? name : partner.name).trim() || 'Partenaire',
            email: isEditing ? email : partner.email,
            phone: isEditing ? phone : partner.phone,
            address: formatAddress(currentAddressNumber, currentAddressType, currentAddressName, currentAddressCity, currentAddressPostalCode),
            notes: isEditing ? notes : partner.notes,
            logoUrl: (isEditing ? logoUrl : partner.logo_url) ? resolveLogoUrl((isEditing ? logoUrl : partner.logo_url) as string) : null,
            types: partner.types.map((t) => t.label),
            // Le référent était saisi dans la fiche mais n'apparaissait nulle part dans le PDF : le modèle
            // fourni par Emeni lui consacre une carte entière à côté des coordonnées (2026-08-24).
            contact: {
                firstName: isEditing ? contactFirstName : partner.contact_first_name || '',
                lastName: isEditing ? contactLastName : partner.contact_last_name || '',
                email: isEditing ? contactEmail : partner.contact_email || '',
                phone: isEditing ? contactPhone : partner.contact_phone || '',
            },
            issuerName: user?.company_name || undefined,
            brandColor: user?.theme_color || undefined,
        });
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (notFound || !partner) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Partenaire introuvable.</p>
                <Button variant="secondary" onClick={() => navigate('/extras-partenaires')}><ArrowLeft size={15} /> Retour</Button>
            </div>
        );
    }

    const totalTypes = partner.types.length;
    const uncategorizedCount = partner.types.filter((t) => !t.category_id).length;

    return (
        <div className="space-y-6 max-w-5xl">
            <button onClick={() => navigate('/extras-partenaires')} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium">
                <ArrowLeft size={15} /> Partenaires Extras
            </button>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6 items-start">
                <div className="space-y-4">
                    <div className="bg-surface rounded-2xl border border-border p-6">
                        {!isEditing ? (
                            <>
                                <div className="flex items-start justify-between gap-3 mb-5">
                                    <div className="w-16 h-16 shrink-0 rounded-xl bg-surface-soft border border-border overflow-hidden flex items-center justify-center">
                                        {partner.logo_url ? (
                                            <img src={resolveLogoUrl(partner.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <HardHat className="text-content-muted" size={26} />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button onClick={handleDownloadPdf} title="Télécharger la fiche en PDF"
                                            className="p-2 rounded-lg border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-colors">
                                            <FileDown size={15} />
                                        </button>
                                        <button onClick={startEdit} title="Modifier la fiche"
                                            className="p-2 rounded-lg border border-blue-500/20 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
                                            <Pencil size={15} />
                                        </button>
                                    </div>
                                </div>
                                <h1 className="text-xl font-bold text-content mb-4 truncate">{partner.name}</h1>
                                <div className="space-y-4">
                                    {/* Section 1: Infos de l'entité */}
                                    <div className="space-y-2.5 text-sm">
                                        <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Infos de l'entité</p>
                                        <div className="flex items-center gap-2.5 text-content">
                                            <Mail size={14} className="text-content-muted shrink-0" />
                                            <span className="truncate">{partner.email || 'Non renseigné'}</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-content">
                                            <Phone size={14} className="text-content-muted shrink-0" />
                                            <span className="truncate">{partner.phone || 'Non renseigné'}</span>
                                        </div>
                                        {(partner.address_number || partner.address_type || partner.address_name || partner.address_city || partner.address_postal_code) && (
                                            <div className="flex items-start gap-2.5 text-content">
                                                <MapPin size={14} className="text-content-muted shrink-0 mt-0.5" />
                                                <span>{formatAddress(partner.address_number, partner.address_type, partner.address_name, partner.address_city, partner.address_postal_code)}</span>
                                            </div>
                                        )}
                                        {partner.notes && (
                                            <div className="flex items-start gap-2.5 text-content-muted pt-2 border-t border-border mt-2">
                                                <StickyNote size={14} className="shrink-0 mt-0.5" />
                                                <span className="text-xs">{partner.notes}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Section 2: Infos du référent contact */}
                                    {(partner.contact_first_name || partner.contact_last_name || partner.contact_phone || partner.contact_email) && (
                                        <div className="space-y-2.5 text-sm pt-2 border-t border-border">
                                            <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Référent contact</p>
                                            {(partner.contact_first_name || partner.contact_last_name) && (
                                                <div className="text-content">
                                                    <span className="font-semibold">{partner.contact_first_name} {partner.contact_last_name}</span>
                                                </div>
                                            )}
                                            {partner.contact_email && (
                                                <div className="flex items-center gap-2.5 text-content">
                                                    <Mail size={14} className="text-content-muted shrink-0" />
                                                    <span className="truncate">{partner.contact_email}</span>
                                                </div>
                                            )}
                                            {partner.contact_phone && (
                                                <div className="flex items-center gap-2.5 text-content">
                                                    <Phone size={14} className="text-content-muted shrink-0" />
                                                    <span className="truncate">{partner.contact_phone}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => navigate(`/extras-partenaires/${id}/demandes`)}
                                    className="w-full mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center hover:opacity-80 transition-opacity"
                                >
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><Send size={13} className="text-content-muted" /> {requestCounts?.toRequest ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">À demander</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><PackageCheck size={13} className="text-content-muted" /> {requestCounts?.requested ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">Demandées</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><BadgeCheck size={13} className="text-content-muted" /> {requestCounts?.confirmed ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">Confirmées</p>
                                    </div>
                                </button>
                            </>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <label className="w-16 h-16 shrink-0 rounded-xl bg-surface-soft border border-border overflow-hidden flex items-center justify-center cursor-pointer relative group">
                                        {logoUrl ? (
                                            <img src={resolveLogoUrl(logoUrl)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <HardHat className="text-content-muted" size={26} />
                                        )}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <ImageIcon size={16} className="text-white" />
                                        </div>
                                        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleLogoUpload} className="hidden" />
                                    </label>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs uppercase tracking-widest text-content-muted font-bold mb-1">Logo</p>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs font-bold text-brand-600 dark:text-brand-400 cursor-pointer hover:underline">
                                                Changer
                                                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleLogoUpload} className="hidden" />
                                            </label>
                                            {logoUrl && (
                                                <button onClick={() => setLogoUrl(null)} className="text-xs font-bold text-red-400 hover:underline flex items-center gap-0.5">
                                                    <X size={12} /> Retirer
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Section 1: Infos de l'entité */}
                                <div className="space-y-3 pb-4 border-b border-border">
                                    <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Infos de l'entité</p>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom du partenaire *</label>
                                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Email</label>
                                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Adresse</label>
                                        <div className="grid grid-cols-[60px_1fr] gap-2">
                                            <Input placeholder="N°" value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} />
                                            <select value={addressType} onChange={(e) => setAddressType(e.target.value)}
                                                className="w-full bg-surface border border-border rounded-md text-content text-sm outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 px-2.5 py-2">
                                                <option value="">Type (rue, bld, chemin...)</option>
                                                {addressType && !ADDRESS_TYPES.includes(addressType) && <option value={addressType}>{addressType}</option>}
                                                {ADDRESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <Input placeholder="Nom de la rue/avenue" value={addressName} onChange={(e) => setAddressName(e.target.value)} />
                                        <div className="grid grid-cols-[100px_1fr] gap-2">
                                            <Input placeholder="Code postal" value={addressPostalCode} onChange={(e) => setAddressPostalCode(e.target.value)} />
                                            <Input placeholder="Ville" value={addressCity} onChange={(e) => setAddressCity(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Notes</label>
                                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Ex: délai de prévenance habituel, conditions..." />
                                    </div>
                                </div>

                                {/* Section 2: Infos du référent contact */}
                                <div className="space-y-3 pt-4">
                                    <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Référent contact</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Prénom</label>
                                            <Input value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom</label>
                                            <Input value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Email</label>
                                        <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                                        <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={saving} className="flex-1">
                                        <X size={15} /> Annuler
                                    </Button>
                                    <Button onClick={handleSave} disabled={saving} className="flex-1">
                                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {saving ? 'Enregistrement...' : 'Enregistrer'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-surface rounded-2xl border border-border p-6 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Package size={13} /> Catalogue de types d'extras</p>
                        <button onClick={() => navigate(`/extras-partenaires/${id}/types`)} className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1 shrink-0">
                            Voir tous les types <ArrowRight size={12} />
                        </button>
                    </div>

                    {totalTypes === 0 && partner.categories.length === 0 ? (
                        <p className="text-xs text-content-muted">Aucun type ni catégorie pour l'instant.</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                            {partner.categories.map((cat) => (
                                <button key={cat.id} onClick={() => navigate(`/extras-partenaires/${id}/types/${cat.id}`)}
                                    className="text-left p-3.5 rounded-xl bg-surface-soft border border-border hover:border-brand-500/40 transition-colors">
                                    <p className="text-sm font-bold text-content truncate">{cat.name}</p>
                                    <p className="text-xs text-content-muted mt-0.5">{cat.type_count} type{cat.type_count > 1 ? 's' : ''}</p>
                                </button>
                            ))}
                            {uncategorizedCount > 0 && (
                                <button onClick={() => navigate(`/extras-partenaires/${id}/types/none`)}
                                    className="text-left p-3.5 rounded-xl bg-surface-soft border border-dashed border-border hover:border-brand-500/40 transition-colors">
                                    <p className="text-sm font-bold text-content truncate">Sans catégorie</p>
                                    <p className="text-xs text-content-muted mt-0.5">{uncategorizedCount} type{uncategorizedCount > 1 ? 's' : ''}</p>
                                </button>
                            )}
                        </div>
                    )}

                    {showAddCategory ? (
                        <div className="flex items-center gap-1.5 pt-1">
                            <input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Nom de la catégorie"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setShowAddCategory(false); }}
                                className="flex-1 min-w-0 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            <Button size="sm" onClick={handleAddCategory} disabled={savingCategory || !newCategoryName.trim()}>
                                {savingCategory ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}><X size={13} /></Button>
                        </div>
                    ) : (
                        <Button variant="secondary" size="sm" onClick={() => setShowAddCategory(true)} className="w-full">
                            <FolderPlus size={14} /> Ajouter une catégorie
                        </Button>
                    )}
                </div>
            </div>

            <div className="max-w-3xl flex items-center gap-2">
                <Button onClick={() => setShowNewRequest(true)}>
                    <Send size={15} /> Faire une demande d'extras
                </Button>
                <Button variant="secondary" onClick={() => setShowEmailSettings(true)}>
                    <Mail size={15} /> Paramétrer les emails de demande
                </Button>
            </div>

            {showNewRequest && (
                <NewExtrasRequestModal
                    partnerId={id!}
                    types={partner.types}
                    onClose={() => setShowNewRequest(false)}
                    onCreated={() => {
                        setShowNewRequest(false);
                        moduleApi.getExtrasPartnerRequests(id!).then((d: any) => {
                            setRequestCounts({
                                toRequest: Array.isArray(d?.a_demander) ? d.a_demander.length : 0,
                                requested: Array.isArray(d?.demandees) ? d.demandees.length : 0,
                                confirmed: Array.isArray(d?.confirmees_par_prestataire) ? d.confirmees_par_prestataire.length : 0,
                            });
                        }).catch(() => {});
                        navigate(`/extras-partenaires/${id}/demandes`);
                    }}
                />
            )}

            <Modal
                open={showEmailSettings}
                onClose={() => setShowEmailSettings(false)}
                title="Paramétrer les emails de demande"
                icon={<Mail size={16} />}
                maxWidth="max-w-2xl"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowEmailSettings(false)}>Annuler</Button>
                        <Button onClick={handleSaveRequestEmail} disabled={savingRequestEmail}>
                            {savingRequestEmail ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {savingRequestEmail ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-1.5 border-b border-border pb-2 flex-wrap">
                        <button onClick={() => setEmailSettingsTab('partenaire')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${emailSettingsTab === 'partenaire' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            Partenaire — sans suggestion
                        </button>
                        <button onClick={() => setEmailSettingsTab('partenaire_suggestion')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${emailSettingsTab === 'partenaire_suggestion' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            Partenaire — avec suggestion
                        </button>
                        <button onClick={() => setEmailSettingsTab('etablissement')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${emailSettingsTab === 'etablissement' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            Vous
                        </button>
                    </div>

                    {emailSettingsTab === 'partenaire' ? (
                        <div className="space-y-2">
                            <p className="text-xs text-content-muted italic">Envoyé à ce partenaire quand AUCUNE personne précise n'est suggérée dans la demande. Laissez vide pour utiliser le modèle par défaut.</p>
                            <NotificationBlock title="Mail au partenaire (sans suggestion)"
                                subject={requestEmailSubject} setSubject={setRequestEmailSubject} body={requestEmailBody} setBody={setRequestEmailBody}
                                subjectRef={requestEmailSubjectRef} bodyRef={requestEmailBodyRef}
                                activeField={requestEmailActiveField} setActiveField={setRequestEmailActiveField}
                                onInsertToken={insertRequestEmailToken} onWrap={wrapRequestEmailBody} tokens={REQUEST_EMAIL_TOKENS} />
                        </div>
                    ) : emailSettingsTab === 'partenaire_suggestion' ? (
                        <div className="space-y-2">
                            <p className="text-xs text-content-muted italic">Envoyé à ce partenaire quand une ou plusieurs personnes précises sont suggérées dans la demande. Modèle totalement indépendant du précédent — laissez vide pour utiliser le modèle par défaut.</p>
                            <NotificationBlock title="Mail au partenaire (avec suggestion)"
                                subject={requestWithSuggestionSubject} setSubject={setRequestWithSuggestionSubject} body={requestWithSuggestionBody} setBody={setRequestWithSuggestionBody}
                                subjectRef={requestWithSuggestionSubjectRef} bodyRef={requestWithSuggestionBodyRef}
                                activeField={requestWithSuggestionActiveField} setActiveField={setRequestWithSuggestionActiveField}
                                onInsertToken={insertRequestWithSuggestionToken} onWrap={wrapRequestWithSuggestionBody} tokens={REQUEST_WITH_SUGGESTION_EMAIL_TOKENS} />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-xs text-content-muted italic">Envoyé à VOUS (l'établissement) dès que ce partenaire confirme sa demande. Laissez vide pour utiliser le modèle par défaut.</p>
                            <NotificationBlock title="Mail reçu quand le partenaire confirme"
                                subject={establishmentConfirmationSubject} setSubject={setEstablishmentConfirmationSubject} body={establishmentConfirmationBody} setBody={setEstablishmentConfirmationBody}
                                subjectRef={establishmentConfirmationSubjectRef} bodyRef={establishmentConfirmationBodyRef}
                                activeField={establishmentConfirmationActiveField} setActiveField={setEstablishmentConfirmationActiveField}
                                onInsertToken={insertEstablishmentConfirmationToken} onWrap={wrapEstablishmentConfirmationBody} tokens={ESTABLISHMENT_CONFIRMATION_TOKENS} />
                        </div>
                    )}

                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                </div>
            </Modal>
        </div>
    );
};
